import React, { FC, useEffect } from 'react'
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Theme, Typography } from '@mui/material'
import { Control, Controller, FieldPath, FieldValues, PathValue } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import { alpha } from '@mui/material/styles'
import { SelectArrow } from '../SelectArrow'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    display: 'flex',
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: 'inherit',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  select: {
    width: 'inherit',
    maxHeight: 500,
    '& .MuiSelect-icon': {
      color: alpha(theme.palette.primary.light, 0.6),
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  box: {
    color: theme.palette.primary.light,
  },
  chip: {
    color: theme.palette.primary.light,
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    '& .MuiChip-deleteIconColorDefault': {
      color: alpha(theme.palette.primary.light, 0.26),
      '&:hover': {
        color: alpha(theme.palette.primary.light, 0.4),
      },
    },
  },
  loading: {
    color: theme.palette.primary.light,
  },
}))

type MultiSelectProps<T extends FieldValues, TName extends FieldPath<T>> = {
  inputName: TName
  labelName: string
  placeholder: string
  control: Control<T>
  loading: boolean
  options: { value: string | number; label: string }[]
  firstLoad: () => void
}

export const MultiSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  inputName,
  labelName,
  placeholder,
  control,
  loading,
  options,
  firstLoad,
}: MultiSelectProps<T, TName>): ReturnType<FC<MultiSelectProps<T, TName>>> => {
  const classes = useStyles()

  useEffect(() => {
    firstLoad()
  }, [firstLoad])

  return (
    <Grid container className={classes.container}>
      <Grid item>
        <InputLabel shrink htmlFor={inputName} className={classes.label}>
          {labelName}
        </InputLabel>
      </Grid>
      <FormControl className={classes.formControl}>
        <Controller
          name={inputName}
          control={control}
          defaultValue={[] as PathValue<T, TName>}
          render={({ field }) => (
            <Select
              {...field}
              className={classes.select}
              multiple
              placeholder={placeholder}
              IconComponent={SelectArrow}
              renderValue={(selected: (string | number)[]) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => (
                    <Chip
                      key={id}
                      label={options.find((option) => option.value === id)?.label || id}
                      className={classes.chip}
                      onDelete={(event) => {
                        event.stopPropagation()
                        field.onChange(selected.filter((selectedId) => selectedId !== id))
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
              {loading && (
                <MenuItem disabled>
                  <Typography variant="body2" className={classes.loading}>
                    Loading...
                  </Typography>
                </MenuItem>
              )}
            </Select>
          )}
        />
      </FormControl>
    </Grid>
  )
}
